/**
 * @module UseUploadReceiptModal
 * @purpose State and submit logic for the UploadReceiptModal component
 * @depends useCollectionTargets, uploadReceipt service, MAX_UPLOAD_BYTES, CollectionTarget
 * @dependedOnBy UploadReceiptModal
 *
 * @rules
 * - Performance: stateless beyond local useState; targets fetched via useCollectionTargets
 * - State: local useState per field; progress 0-100 via XHR onProgress callback
 * - Errors: TARGET_ALREADY_RECEIVED mapped to alreadyExists step; evidence_not_received and
 *   network/5xx mapped to error step (evidence_not_received reuses the gate's warning copy)
 */

import { useState, useCallback } from 'react'
import type { TFunction } from 'i18next'
import type { CollectionTarget } from '../../../types/collection-targets'
import type { CollectionReceipt } from '../../../types/collection-receipts'
import type { UploadReceiptMetadata } from '../../../services/collection-receipts'
import { MAX_UPLOAD_BYTES, UploadReceiptError, uploadReceipt } from '../../../services/collection-receipts'
import type { OperationProgress, ProbeReceiptResponse } from '../../../types/evidence-containers'
import { useCollectionTargets } from '../../../hooks'
import { useStagedFlow } from './upload-receipt-modal.staged-flow'
import type { StagedStep } from './upload-receipt-modal.staged'

export type UploadStep =
  | 'pick'
  | 'uploading'
  | 'success'
  | 'error'
  | 'alreadyExists'
  | 'duplicateHash'

const IN_SCOPE_STATUSES = new Set(['pending', 'sent', 'in_progress', 'partial'])
const ALREADY_RECEIVED_CODE = 'TARGET_ALREADY_RECEIVED'
const EVIDENCE_NOT_RECEIVED_CODE = 'evidence_not_received'
const STORAGE_TARGET_EXISTS_CODE = 'STORAGE_TARGET_EXISTS'
/**
 * Server accepted the duplicate as overridable. A plain DUPLICATE_HASH stays a
 * hard stop -- the override only exists where the operator enabled it.
 */
const DUPLICATE_OVERRIDABLE_CODE = 'DUPLICATE_HASH_OVERRIDABLE'
const BYTES_PER_KIB = 1024
const BYTES_PER_GIB = BYTES_PER_KIB * BYTES_PER_KIB * BYTES_PER_KIB

/**
 * Whether a target should appear in the picker's default (non-"show all") view.
 *
 * @behaviour Targets in the traditional in-scope statuses remain visible, plus any
 *            CoC-received target that has not yet been uploaded -- `targetStatus ===
 *            'returned'` now means "received", not "already uploaded", so it must not
 *            be hidden from the picker by default (that was the C5 upload-gate bug).
 */
function isEligibleForDefaultView(target: CollectionTarget): boolean {
  if (IN_SCOPE_STATUSES.has(target.targetStatus)) return true
  return target.evidenceReceived && !target.hasReceipt
}

export interface UploadReceiptModalState {
  step: UploadStep
  target: CollectionTarget | null
  zipFile: File | null
  logFile: File | null
  oobHash: string
  notes: string
  progress: number
  error: string | null
  showAll: boolean
  targets: CollectionTarget[]
  targetsLoading: boolean
  uploading: boolean
  setTarget: (target: CollectionTarget | null) => void
  setZipFile: (file: File | null) => void
  setLogFile: (file: File | null) => void
  setOobHash: (value: string) => void
  setNotes: (value: string) => void
  toggleShowAll: () => void
  /** Re-submit the same file with an explicit duplicate override. */
  handleConfirmDuplicate: () => Promise<void>
  handleSubmit: () => Promise<void>
  handleRetry: () => void
  /** transport probe (spec FR-staged): fires whenever `zipFile` changes */
  transportProbe: ProbeReceiptResponse | null
  probing: boolean
  probeError: string | null
  retryProbe: () => void
  stagedStep: StagedStep
  stagedOperationId: string | null
  stagedError: string | null
  stagedAlreadyExists: boolean
  handleRegisterStaged: () => Promise<void>
  handleStagedVerifyComplete: (progress: OperationProgress) => Promise<void>
  retryStagedVerification: () => Promise<void>
  resetStaged: () => void
}

interface UseUploadReceiptModalArgs {
  caseId: string
  requestId: string
  onSuccess: (receipt: CollectionReceipt) => void
  t: TFunction<'cases'>
}

function buildLimitLabel(): string {
  return `${(MAX_UPLOAD_BYTES / BYTES_PER_GIB).toFixed(0)} GiB`
}

function isAlreadyExistsError(err: unknown): boolean {
  if (err instanceof UploadReceiptError) return err.code === ALREADY_RECEIVED_CODE
  return err instanceof Error && err.message.includes(ALREADY_RECEIVED_CODE)
}

function isEvidenceNotReceivedError(err: unknown): boolean {
  return err instanceof UploadReceiptError && err.code === EVIDENCE_NOT_RECEIVED_CODE
}

function isStorageTargetExistsError(err: unknown): boolean {
  return err instanceof UploadReceiptError && err.code === STORAGE_TARGET_EXISTS_CODE
}

function isOverridableDuplicateError(err: unknown): boolean {
  return err instanceof UploadReceiptError && err.code === DUPLICATE_OVERRIDABLE_CODE
}

interface UploadErrorOutcome {
  step: UploadStep
  message: string
}

function classifyUploadError(err: unknown, t: TFunction<'cases'>): UploadErrorOutcome {
  if (isAlreadyExistsError(err)) {
    return { step: 'alreadyExists', message: t('detail.collections.uploadReceipt.alreadyExists') }
  }
  if (isOverridableDuplicateError(err)) {
    return { step: 'duplicateHash', message: t('detail.collections.uploadReceipt.duplicateHashWarning') }
  }
  if (isEvidenceNotReceivedError(err)) {
    return { step: 'error', message: t('detail.collections.uploadReceipt.receiveFirstWarning') }
  }
  if (isStorageTargetExistsError(err)) {
    return { step: 'error', message: t('detail.collections.uploadReceipt.storageTargetExists') }
  }
  const message = err instanceof Error ? err.message : 'Upload failed'
  return { step: 'error', message: t('detail.collections.uploadReceipt.serverError', { message }) }
}

function buildMetadata(args: {
  target: CollectionTarget
  oobHash: string
  notes: string
  allowDuplicate: boolean
}): UploadReceiptMetadata {
  const { target, oobHash, notes, allowDuplicate } = args
  return {
    collectionTargetId: target.id,
    receivedFromName: target.endpointHostname,
    receivedMethod: 'other',
    ...(oobHash ? { oobZipSha256: oobHash } : {}),
    ...(notes ? { notes } : {}),
    ...(allowDuplicate ? { allowDuplicate: true } : {}),
  }
}

function validateZipFile(file: File, t: TFunction<'cases'>): string | null {
  if (!file.name.toLowerCase().endsWith('.zip')) {
    return t('detail.collections.uploadReceipt.notZip')
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return t('detail.collections.uploadReceipt.tooLarge', { limit: buildLimitLabel() })
  }
  return null
}

interface PerformUploadArgs {
  caseId: string
  requestId: string
  zipFile: File
  logFile: File | null
  target: CollectionTarget
  oobHash: string
  notes: string
  allowDuplicate: boolean
  onProgress: (percent: number) => void
}

async function performUpload(args: PerformUploadArgs): Promise<CollectionReceipt> {
  const { caseId, requestId, zipFile, logFile, target, oobHash, notes, allowDuplicate, onProgress } =
    args
  return uploadReceipt({
    caseId,
    requestId,
    file: zipFile,
    ...(logFile !== null ? { logFile } : {}),
    metadata: buildMetadata({ target, oobHash, notes, allowDuplicate }),
    onProgress,
  })
}

interface SubmitUploadDeps {
  caseId: string
  requestId: string
  zipFile: File
  logFile: File | null
  target: CollectionTarget
  oobHash: string
  notes: string
  t: TFunction<'cases'>
  onSuccess: (receipt: CollectionReceipt) => void
  onError: (err: unknown) => void
  setStep: React.Dispatch<React.SetStateAction<UploadStep>>
  setProgress: React.Dispatch<React.SetStateAction<number>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
  setUploading: React.Dispatch<React.SetStateAction<boolean>>
}

async function executeSubmitUpload(
  deps: SubmitUploadDeps,
  allowDuplicate = false,
): Promise<void> {
  const { caseId, requestId, zipFile, logFile, target, oobHash, notes, onSuccess, onError, setStep, setProgress, setError, setUploading } = deps
  setStep('uploading')
  setProgress(0)
  setError(null)
  setUploading(true)
  try {
    const receipt = await performUpload({
      caseId, requestId, zipFile, logFile, target, oobHash, notes, allowDuplicate,
      onProgress: (percent) => { setProgress(percent) },
    })
    setStep('success')
    setUploading(false)
    onSuccess(receipt)
  } catch (err) {
    onError(err)
  }
}

/**
 * State and behaviour for UploadReceiptModal.
 *
 * @behaviour Validates zip type and file size client-side before calling uploadReceipt.
 *            Maps TARGET_ALREADY_RECEIVED to alreadyExists step; evidence_not_received and
 *            other errors to error step. The default (non-"show all") target list includes
 *            CoC-received-but-not-uploaded targets, not just the traditional in-scope statuses.
 * @errors Sets step to error or alreadyExists on failure; never throws from hook body.
 */
export function useUploadReceiptModal(args: UseUploadReceiptModalArgs): UploadReceiptModalState {
  const { caseId, requestId, onSuccess, t } = args
  const [step, setStep] = useState<UploadStep>('pick')
  const [target, setTarget] = useState<CollectionTarget | null>(null)
  const [zipFile, setZipFile] = useState<File | null>(null)
  const [logFile, setLogFile] = useState<File | null>(null)
  const [oobHash, setOobHash] = useState('')
  const [notes, setNotes] = useState('')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [uploading, setUploading] = useState(false)

  const { targets, loading: targetsLoading } = useCollectionTargets(caseId, requestId)
  const filteredTargets = showAll
    ? targets
    : targets.filter((tgt) => isEligibleForDefaultView(tgt))

  const stagedFlow = useStagedFlow({ caseId, requestId, zipFile, target, oobHash, notes, onSuccess, t })

  const handleError = useCallback(
    (err: unknown) => {
      setUploading(false)
      const outcome = classifyUploadError(err, t)
      setStep(outcome.step)
      setError(outcome.message)
    },
    [t]
  )

  const handleSubmit = useCallback(async () => {
    if (!zipFile || !target) return
    const validationError = validateZipFile(zipFile, t)
    if (validationError) { setError(validationError); return }
    await executeSubmitUpload({
      caseId, requestId, zipFile, logFile, target, oobHash, notes, t,
      onSuccess, onError: handleError, setStep, setProgress, setError, setUploading,
    })
  }, [zipFile, target, caseId, requestId, oobHash, notes, logFile, onSuccess, t, handleError])

  /**
   * Re-submits the SAME file with an explicit duplicate override after the
   * analyst has acknowledged the warning. Only reachable from the
   * `duplicateHash` step, which the server only produces where the override
   * is enabled.
   */
  const handleConfirmDuplicate = useCallback(async () => {
    if (!zipFile || !target) return
    await executeSubmitUpload(
      {
        caseId, requestId, zipFile, logFile, target, oobHash, notes, t,
        onSuccess, onError: handleError, setStep, setProgress, setError, setUploading,
      },
      true,
    )
  }, [zipFile, target, caseId, requestId, oobHash, notes, logFile, onSuccess, t, handleError])

  const handleRetry = useCallback(() => {
    setStep('pick')
    setError(null)
    setProgress(0)
  }, [])
  const toggleShowAll = useCallback(() => { setShowAll((prev) => !prev) }, [])

  return {
    step, target, zipFile, logFile, oobHash, notes, progress, error, showAll,
    targets: filteredTargets, targetsLoading, uploading,
    setTarget, setZipFile, setLogFile, setOobHash, setNotes,
    toggleShowAll, handleSubmit, handleRetry, handleConfirmDuplicate,
    ...stagedFlow,
  }
}
